/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { OnChangeEventContext } from 'react-crane'
import LockIcon from '@mui/icons-material/Lock'
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'

import {
  BoardGroupFragment,
  PlanItemDetailsFragment,
  useAcademicPlanningSettingsQuery
} from '../../../../generated/graphql'
import { BP_SMALL_UP } from '../../../../lib/styles/breakpoints'
import { errorTextColor, lightBlueBackground } from '../../../../lib/styles/colors'
import Banner from '../../../shared/alerts/Banner'
import { DataField } from '../../../shared/data'
import { Select } from '../../../shared/input'
import { formatCredits } from '../../../../lib/utils'
import { DetailsPlanAlert } from '../../../../types'
import formatTemplateLabels from '../../../templates/utils'
import Switch from '../../../shared/input/Switch/Switch'
import useAppSettings from '../../../../lib/hooks/useAppSettings'
import PlanItemAlerts from '../PlanItemAlerts'
import { subtitle1Css } from '../../../../lib/styles/typography'

type CourseFormProps = {
  /** List of alerts associated the item ready to display */
  alerts: Array<DetailsPlanAlert>
  /** Label to use for campus */
  campusLabel: string
  /** Label to use for course */
  courseLabel: string
  /** Label to use for credits */
  creditsLabel: string
  /** Whether the group field should be disabled */
  disableGroupChange?: boolean
  /** Current GroupId of Plan Item */
  groupId: string | null | undefined
  /** Flag to determine whether or not to display the Requisite(s) label as error text */
  hasRequisiteError?: boolean
  /** If this course form is to show details for selected course info for a placeholder */
  isCoursePlaceholderSelection?: boolean
  /** Whether the user is a student */
  isStudent: boolean
  /** Whether the item has been locked */
  locked: boolean
  /** Event callback input/select in form has changed  */
  onChange: (name: string, value: BoardGroupFragment | boolean) => void
  /** Event callback when the delete button is clicked in the Alert Banner */
  onDelete?: () => void
  /** All Plan Groups for Current Plan */
  planGroups: Array<BoardGroupFragment>
  /** The Current Plan Item */
  planItem: PlanItemDetailsFragment
  /** Whether the plan is read-only */
  readOnly: boolean
  /** Whether the item has been required */
  required: boolean
  /** Label to use for term */
  termLabel: string
}

const defaultProps: Pick<
  CourseFormProps,
  'isCoursePlaceholderSelection' | 'onDelete' | 'hasRequisiteError' | 'disableGroupChange'
> = {
  isCoursePlaceholderSelection: false,
  onDelete: undefined,
  hasRequisiteError: false,
  disableGroupChange: false
}

const bodyCss = css`
  display: grid;

  @media (${BP_SMALL_UP}) {
    grid-template-columns: 380px 1fr;
    column-gap: 3rem;
  }
`
const bannerContainerCss = css`
  grid-column: 1 / 3;
  padding-bottom: 22px;

  .requiredBanner {
    justify-content: left;
    justify-items: left;
  }

  div:not(:last-child) {
    padding-bottom: 8px;
  }
`
const selectCss = css`
  margin-left: 0;
  width: 280px;
`
const descCss = css`
  padding-top: 10px;
`
const detailsCss = css`
  padding-bottom: 16px;
`
const switchCss = css`
  margin-top: -14px;
  margin-left: -22px;

  .switchLabel {
    ${subtitle1Css};
    font-weight: normal;
  }
`

export const CourseForm = ({
  alerts,
  campusLabel,
  courseLabel,
  creditsLabel,
  disableGroupChange,
  groupId,
  hasRequisiteError,
  isCoursePlaceholderSelection,
  isStudent,
  locked,
  onChange,
  onDelete,
  planGroups,
  planItem,
  readOnly,
  required,
  termLabel
}: CourseFormProps) => {
  const {
    campusDescription,
    career,
    creditsMax,
    creditsMin,
    description,
    externalCourseId,
    inProgress,
    requisiteDescription,
    requisiteDescriptionLong,
    requirementTitles,
    typicallyOffered,
    usedToMeetRequirement
  } = planItem
  const { text } = useAppSettings()
  const detailsQuery = useAcademicPlanningSettingsQuery()
  const detailConfig = detailsQuery?.data?.academicPlanningSettings.templateDetailOptions
  const hideTermsOffered = detailsQuery?.data?.academicPlanningSettings.hideTermsOffered
  const hideRequisitesOnPlanItemDetails =
    detailsQuery?.data?.academicPlanningSettings.hideRequisitesOnPlanItemDetails
  const labelConfig = formatTemplateLabels(detailConfig, text.campus)
  const descr = description || 'none'
  const termsOffered = typicallyOffered || 'unknown'
  const requisite = requisiteDescriptionLong || requisiteDescription || 'none'
  const campusDescr = campusDescription || 'none'
  const advisorLabel = text.advisor

  const onGroupChange = (e: OnChangeEventContext) => {
    const group = e.value as BoardGroupFragment
    onChange(e.name, group)
  }
  const onLockChange = () => {
    onChange('locked', !locked)
  }
  const onRequiredChange = () => {
    onChange('required', !required)
  }

  const formattedCredits = formatCredits(creditsMin || undefined, creditsMax || undefined)
  const prereqCss = css`
    ${detailsCss};
    ${hasRequisiteError &&
    `  .requisiteLabel > span {
        color: ${errorTextColor};
      }
    `}
  `

  return (
    <div
      css={bodyCss}
      // meta-data attributes strictly for internal identification purposes
      external-course-id={externalCourseId || 'null'}
      campus-description={campusDescription || 'null'}
      career-id={career || 'null'}
    >
      <PlanItemAlerts
        alerts={alerts}
        courseLabel={text.course}
        locked={locked}
        isCoursePlaceholder={isCoursePlaceholderSelection || false}
        onDelete={onDelete}
        readOnly={readOnly}
        required={required}
      />
      {isStudent && locked && !isCoursePlaceholderSelection && (
        <div css={bannerContainerCss}>
          <Banner icon={<LockIcon />} backgroundColor={lightBlueBackground}>
            This item is locked on your plan by your {advisorLabel.toLowerCase()}. You cannot move
            it to another {termLabel.toLowerCase()}.
          </Banner>
        </div>
      )}
      {isStudent && required && !isCoursePlaceholderSelection && (
        <div css={bannerContainerCss}>
          <Banner
            icon={<AssignmentLateIcon />}
            backgroundColor={lightBlueBackground}
            className="requiredBanner"
          >
            This item is required and cannot be deleted.
          </Banner>
        </div>
      )}
      <div>
        {!isCoursePlaceholderSelection && (
          <Select
            id={termLabel.toLowerCase()}
            css={selectCss}
            disabled={(locked && isStudent) || readOnly || disableGroupChange}
            label={termLabel}
            labelKey="title"
            name="group"
            onChange={onGroupChange}
            options={planGroups}
            value={groupId}
            valueKey="id"
          />
        )}
        <div css={descCss}>
          <DataField label="Description">{descr}</DataField>
        </div>
      </div>
      <div>
        {!isStudent && !isCoursePlaceholderSelection && (
          <div css={switchCss}>
            <Switch
              ariaLabel={`Require this ${courseLabel.toLowerCase()}`}
              checked={required}
              disabled={isStudent || readOnly}
              id="required"
              infoText={`Requiring an item means a student cannot delete it, but it can be moved to another ${termLabel.toLowerCase()}.`}
              label="Required Item"
              labelCssClassName="switchLabel"
              onChange={onRequiredChange}
            />
            <Switch
              ariaLabel={`Lock this ${courseLabel.toLowerCase()}`}
              checked={locked}
              disabled={isStudent || readOnly}
              id="locked"
              infoText={`Locking an item means a student cannot delete it or move it to another ${termLabel.toLowerCase()}.`}
              label="Locked Item"
              labelCssClassName="switchLabel"
              onChange={onLockChange}
            />
          </div>
        )}
        <div css={detailsCss}>
          <DataField label={creditsLabel} useRawValue>
            {formattedCredits}
          </DataField>
        </div>
        {labelConfig && labelConfig.campus && (
          <div css={detailsCss}>
            <DataField label={`${campusLabel}`}>{campusDescr}</DataField>
          </div>
        )}
        <div css={detailsCss}>
          {hideTermsOffered || (
            <DataField label={`${termLabel}(s) Offered`}>{termsOffered}</DataField>
          )}
        </div>
        {hideRequisitesOnPlanItemDetails || (
          <div css={prereqCss}>
            <DataField className="requisiteLabel" label="Requisite(s)">
              {requisite}
            </DataField>
          </div>
        )}
        {!!requirementTitles?.length && (!!usedToMeetRequirement || !!inProgress) && (
          <div css={detailsCss}>
            <DataField label="Requirement(s) Satisfied">{requirementTitles.join(', ')}</DataField>
          </div>
        )}
      </div>
    </div>
  )
}

CourseForm.defaultProps = defaultProps

export default CourseForm
